/** Styles for the chat screen and its components. */
import { StyleSheet } from 'react-native';

import { reviewDesk } from '@/design-system';

/** The thread is the chat column's only scroller (CHAT-1). */
export const threadScrollStyle = {
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  touchAction: 'pan-y',
  overscrollBehavior: 'contain',
} as any;

export const webChatStyles = StyleSheet.create({
  stateCard: {
    width: '100%',
    minHeight: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 14,
  },
  stateTitle: {
    color: reviewDesk.ink,
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
  },
  root: {
    flex: 1,
    flexDirection: 'row',
    gap: 14,
    width: '100%',
    minHeight: 0,
  },
  mainColumn: {
    flex: 1,
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    overflow: 'hidden',
  },
  reviewHeader: {
    minHeight: 84,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFDF9',
  },
  reviewHeaderCompact: {
    minHeight: 70,
    paddingHorizontal: 18,
    paddingVertical: 10,
  },
  // Phones keep title and actions on one row so the thread gets the height
  // (VIS-CORE-8).
  reviewHeaderTiny: {
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  reviewHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  reviewTitle: {
    color: reviewDesk.ink,
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.2,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  statusRowCompact: {
    marginTop: 6,
  },
  statusChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    backgroundColor: reviewDesk.accentSoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  statusChipCompact: {
    paddingVertical: 4,
  },
  statusChipPhone: {
    maxWidth: '100%',
    flexShrink: 1,
  },
  statusChipText: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
  },
  statusChipTextPhone: {
    flexShrink: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerActionsCompact: {
    flexWrap: 'wrap',
  },
  headerIconButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  usageStrip: {
    flexShrink: 0,
    flexDirection: 'row',
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  usageStripCompact: {
    flexWrap: 'wrap',
  },
  usageRowPhone: {
    flexShrink: 0,
    flexDirection: 'row',
    gap: 6,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  usageCellPhone: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  usageCellPhoneText: {
    flexShrink: 1,
    minWidth: 0,
    color: reviewDesk.ink,
    fontSize: 12,
    fontWeight: '700',
  },
  usageCard: {
    flex: 1,
    minWidth: 150,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 11,
  },
  usageCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 8,
  },
  usageIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
  },
  usageLabel: {
    flex: 1,
    color: reviewDesk.inkSoft,
    fontSize: 12,
    fontWeight: '800',
  },
  usageValue: {
    color: reviewDesk.ink,
    fontSize: 16,
    fontWeight: '900',
  },
  usageMeta: {
    color: reviewDesk.muted,
    fontSize: 11,
    fontWeight: '700',
    marginTop: 2,
  },
  usageProgressTrack: {
    height: 5,
    borderRadius: 999,
    backgroundColor: reviewDesk.accentWash,
    overflow: 'hidden',
    marginTop: 9,
  },
  usageProgressFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: reviewDesk.accent,
  },
  thread: {
    flex: 1,
    minHeight: 0,
    backgroundColor: reviewDesk.canvas,
  },
  emptyThread: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 36,
  },
  emptyThreadCompact: {
    padding: 20,
  },
  emptyUploadIcon: {
    width: 82,
    height: 82,
    borderRadius: 41,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.accentWash,
    marginBottom: 18,
  },
  emptyTitle: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  emptyCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 430,
    marginTop: 8,
  },
  emptyActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
  },
  emptyActionsCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  primaryReviewButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 9,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 18,
  },
  primaryReviewButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  secondaryReviewButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    paddingHorizontal: 16,
  },
  secondaryReviewButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  messagesContent: {
    padding: 26,
    paddingBottom: 28,
  },
  messagesContentCompact: {
    padding: 18,
    paddingBottom: 24,
  },
  messagesContentPhone: {
    padding: 12,
    paddingBottom: 20,
  },
  composerWrap: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    paddingHorizontal: 18,
    paddingTop: 14,
    paddingBottom: 14,
  },
  composerWrapPhone: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
  },
  limitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 9,
    backgroundColor: reviewDesk.amberSoft,
    padding: 10,
    marginBottom: 10,
  },
  limitBannerText: {
    flex: 1,
    color: '#8A5B09',
    fontSize: 13,
    fontWeight: '700',
  },
  suggestionStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 10,
  },
  suggestionChip: {
    minHeight: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Phones: one horizontally scrolling row of suggestions.
  suggestionStripPhone: {
    flexWrap: 'nowrap',
    paddingRight: 12,
  },
  suggestionChipPhone: {
    flexShrink: 0,
  },
  suggestionChipText: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
  },
  suggestionChipTextPhone: {
    whiteSpace: 'nowrap',
  } as any,
  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 10,
  },
  composerPhone: {
    width: '100%',
    minWidth: 0,
  },
  composerInput: {
    flex: 1,
    minHeight: 46,
    maxHeight: 110,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    color: reviewDesk.ink,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    lineHeight: 21,
  },
  composerInputPhone: {
    minWidth: 0,
  },
  sendButton: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accent,
  },
  sendButtonDisabled: {
    backgroundColor: '#B8C1CF',
  },
  disclaimer: {
    color: reviewDesk.muted,
    fontSize: 11,
    textAlign: 'center',
    marginTop: 8,
  },
  contextPanel: {
    width: 292,
    flexShrink: 0,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 16,
    gap: 14,
  },
  contextPanelCompact: {
    width: '100%',
    marginBottom: 14,
  },
  contextPanelPhone: {
    maxWidth: '100%',
    minWidth: 0,
  },
  contextTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
  },
  contextFileCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    padding: 12,
  },
  contextFileTitle: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  contextMeta: {
    color: reviewDesk.muted,
    fontSize: 12,
    marginTop: 2,
  },
  contextSection: {
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    paddingTop: 12,
    gap: 10,
  },
  contextSectionTitle: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '800',
    marginBottom: 2,
  },
  contextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  contextRowPhone: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  contextLabel: {
    color: reviewDesk.muted,
    fontSize: 12,
    fontWeight: '600',
  },
  contextValue: {
    color: reviewDesk.ink,
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'right',
  },
  contextValuePhone: {
    textAlign: 'left',
  },
  downloadButton: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
  },
  downloadButtonText: {
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '800',
  },
  contextNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    borderRadius: 10,
    backgroundColor: reviewDesk.amberSoft,
    padding: 12,
    marginTop: 'auto',
  },
  contextNoteText: {
    flex: 1,
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '600',
  },
});
